/**
 * Where a supplier's email address comes from.
 *
 * Measured 2026-09-22: `masters.get_suppliers` carries `primary_email: ""` on all 2,900 rows,
 * and `items.get_supplier_emails` — the one lookup the app used — answers an address for 4 of 15
 * suppliers that have one, because it reads a "primary contact" flag that is set on no supplier
 * contact at all. The addresses live on `Contact` documents linked to the supplier through
 * `Dynamic Link`: 1,633 suppliers have one, 1,603 exactly one. That is where the app already
 * reads customers' addresses (`useCustomerContacts.ts`), and it is the fallback here.
 *
 * The server's answer still wins when it has one — a backfill (`data_ops.import_supplier_email`)
 * may set the flag, and then the two agree. Every distinct contact address goes in `to`, primary
 * first: the 30 suppliers with several list `sales@` / `sales2@`-style siblings, all meant for us.
 */
import { callGet } from './bosun'
import { getResource } from './frappe'
import { pickRecipients, type WireContact } from './useCustomerContacts'
import { trimmed } from './wire'

export interface SupplierAddresses {
  to: string[]
  cc: string[]
}

export interface SupplierLookupDeps {
  serverLookup: (supplier: string) => Promise<{ supplier_name?: string | null; email?: string | null } | null>
  contactsOf: (supplier: string) => Promise<WireContact[]>
}

const LIVE: SupplierLookupDeps = {
  serverLookup: (supplier) => callGet('items', 'get_supplier_emails', { supplier }),
  contactsOf: async (supplier) =>
    (
      await getResource<WireContact>('Contact', {
        filters: [
          ['Dynamic Link', 'link_name', '=', supplier],
          ['Dynamic Link', 'link_doctype', '=', 'Supplier'],
        ],
        fields: ['name', 'email_id', 'is_primary_contact'],
        limit: 20,
      })
    ).data,
}

/** The addresses to send to, or `null` when neither source knows one. */
export async function lookupSupplierAddresses(supplier: string, deps: SupplierLookupDeps = LIVE): Promise<SupplierAddresses | null> {
  let fromServer = ''
  try {
    // `trimmed()`: the wire carries the leading-space defect (' dhivya@avu.net.in' observed live).
    fromServer = trimmed((await deps.serverLookup(supplier))?.email)
  } catch {
    fromServer = ''
  }
  if (fromServer !== '') return { to: [fromServer], cc: [] }
  let contacts: WireContact[] = []
  try {
    contacts = await deps.contactsOf(supplier)
  } catch {
    contacts = []
  }
  const addresses = pickRecipients(contacts)
  return addresses.length > 0 ? { to: addresses, cc: [] } : null
}
